import { motion, AnimatePresence } from 'framer-motion';
import type { AccountingFilters, PeriodFilter } from '@/hooks/useAccounting';
import { theme } from '@/lib/theme';

interface Props {
  filters: AccountingFilters;
  onUpdate: (f: Partial<AccountingFilters>) => void;
  total: number;
  loading: boolean;
}

const PERIODS: { value: PeriodFilter; label: string; icon: string }[] = [
  { value: 'today', label: 'Hoy', icon: 'ri-sun-line' },
  { value: 'week', label: 'Esta semana', icon: 'ri-calendar-check-line' },
  { value: 'month', label: 'Este mes', icon: 'ri-calendar-line' },
  { value: 'year', label: 'Este año', icon: 'ri-calendar-2-line' },
  { value: 'custom', label: 'Personalizado', icon: 'ri-filter-3-line' },
];

export default function AccountingFilters({ filters, onUpdate, total, loading }: Props) {
  return (
    <div className="flex flex-col gap-3">
      {/* Period selector */}
      <div className="flex items-center gap-2 flex-wrap">
        <div
          className="flex items-center p-1 rounded-xl gap-0.5"
          style={{ backgroundColor: theme.colors.bgSurface, border: `1px solid ${theme.colors.border}` }}
        >
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => onUpdate({ period: p.value })}
              className="relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap"
              style={{
                color: filters.period === p.value ? theme.colors.textPrimary : theme.colors.textMuted,
              }}
            >
              {filters.period === p.value && (
                <motion.div
                  layoutId="period-indicator"
                  className="absolute inset-0 rounded-lg"
                  style={{
                    backgroundColor: 'rgba(52,211,153,0.10)',
                    border: '1px solid rgba(52,211,153,0.22)',
                  }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
              <div className="w-3.5 h-3.5 flex items-center justify-center relative z-10">
                <i
                  className={`${p.icon} text-xs`}
                  style={{ color: filters.period === p.value ? '#34d399' : theme.colors.textMuted }}
                />
              </div>
              <span className="relative z-10">{p.label}</span>
            </button>
          ))}
        </div>

        {/* Transaction count badge */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-xl border ml-auto"
          style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
        >
          {loading ? (
            <div className="w-20 h-3 bg-white/8 rounded animate-pulse" />
          ) : (
            <>
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-receipt-line text-xs" style={{ color: '#34d399' }} />
              </div>
              <span className="text-xs" style={{ color: theme.colors.textMuted }}>
                <span className="font-semibold" style={{ color: theme.colors.textPrimary }}>{total}</span>
                {' '}transaccion{total !== 1 ? 'es' : ''}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Custom date range */}
      <AnimatePresence>
        {filters.period === 'custom' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-xs whitespace-nowrap" style={{ color: theme.colors.textMuted }}>
                  Desde
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => onUpdate({ dateFrom: e.target.value })}
                  className="text-xs px-3 py-2 rounded-lg border outline-none transition-all"
                  style={{
                    backgroundColor: theme.colors.bgSurface,
                    borderColor: theme.colors.border,
                    color: theme.colors.textPrimary,
                    colorScheme: 'dark',
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs whitespace-nowrap" style={{ color: theme.colors.textMuted }}>
                  Hasta
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => onUpdate({ dateTo: e.target.value })}
                  className="text-xs px-3 py-2 rounded-lg border outline-none transition-all"
                  style={{
                    backgroundColor: theme.colors.bgSurface,
                    borderColor: theme.colors.border,
                    color: theme.colors.textPrimary,
                    colorScheme: 'dark',
                  }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
